import { Grid, InputLabel } from '@material-ui/core';
import React, { useEffect, useRef, useState } from 'react';
import { Auto, CheckBox, NumberText, RadioGroup } from '../../core/controls';
import { EmResult } from '../../core/domain';
import {
  Driver,
  EmCalculatorConfiguration,
  EmCalculatorStore,
  NoteType,
} from '../../core/em-calculator-api';
import { Control, formApi } from '../../core/form';
import { IcdCptApi } from '../../core/icd-cpt-api';
import { CRITICAL_CARE_CODE_2 } from '../../core/procedure';
import { lightGray } from '../../core/theme';
import { WorksheetContext } from '../../core/visit-context';
import { AddButton } from '../common/navigation';
import { FieldValue } from '../common/presentation';

interface EmCalculatorProps {
  api: EmCalculatorStore;
  context?: WorksheetContext;
  icdCpt?: IcdCptApi;
}

type ServiceCategoriesProps = EmCalculatorProps & {
  conf: EmCalculatorConfiguration;
  onDriverChange: (v: Driver) => void;
  onCategoryChange: () => void;
};

function serviceCategories(props: ServiceCategoriesProps) {
  const { onDriverChange, onCategoryChange, api } = props;
  const { supportedNotesAndDrivers } = props.conf;

  const ref = api((s) => s.request);
  const update = () => api.getState().setRequest(ref);

  function setNoteType(t: NoteType) {
    ref.emDriver = null;
    ref.noppCategory = null;

    if (t && t.drivers) {
      setDrivers(t.drivers);

      if (t.drivers.length === 1) {
        const driver = t.drivers[0];
        ref.emDriver = driver.driverName;

        setDriver(driver);
        setNoppList(driver.noppList || []);
        api.getState().updateMinMax(driver);
        onDriverChange(driver);
      } else {
        setDriver(null);
        setNoppList([]);
      }
      ref.noteType = t.noteType;
    }

    onCategoryChange();
  }
  const disabled = api((s) => s.readonly);

  const form = formApi({
    disabled,
    values: {
      noteType: null,
      emDriver: null,
      noppCategory: null,
    },
    onChange: () => {
      update();
    },
    fields: {
      noteType: {
        label: 'E/M Service Category',
        list: supportedNotesAndDrivers,
        listLabel: (v) => v.noteDescription,
        onChange: setNoteType,
      },
      emDriver: {
        label: 'E/M Sub Category',
        list: [],
        listLabel: (v) => v.driverDescription,
        onChange: (t: Driver) => {
          //  console.log('onChange DRIVER', t)
          api.getState().updateMinMax(t);

          ref.emDriver = t?.driverName;
          ref.noppCategory = null;
          if (t) {
            setNoppList(t.noppList || []);
            setNoppCategory('');
          } else {
            setNoppList([]);
          }
          onDriverChange({
            ...ref,
            ...{ timeRangeList: t ? t.timeRangeList || [] : [] },
          } as any);
        },
      },
      noppCategory: {
        style: { pointerEvents: disabled ? 'none' : undefined },
        label: 'Nature of Presenting Problem',
        list: [],
        listLabel: (v) => v,
        onChange: (v) => {
          if (v) {
            ref.noppCategory = v;
            update();
            api.getState().calculateNopp();
          } else {
            ref.noppCategory = undefined;
            update();
          }
        },
      },
    },
  });

  const { emDriver, noppCategory, noteType } = form.controls;

  const setDriver = emDriver.value((s) => s.setValue);
  const setNoppCategory = noppCategory.value((s) => s.setValue);
  const disableNopp = noppCategory.disabled((s) => s.setValue);

  const setDrivers = emDriver.list((s) => s.setValue);

  const noppList = noppCategory.list((s) => s.setValue);
  const setNoppList = (value: any[]) => {
    //  console.log('set nopp list enabled null', !value || value.length < 1, value);
    noppList(value || []);
    noppCategory.value.setState({ value: null });
    disableNopp(!value || value.length < 1);
  };

  form.reset = () => {
    const drivers = supportedNotesAndDrivers;

    if (drivers?.length) {
      const value = drivers[0];
      noteType.value.setState({ value });

      setNoteType(value);
      update();
    }
  };

  return form;
}

function ServiceCategories({ form, nopp, api }) {
  const { noteType, emDriver, noppCategory } = form.controls;
  const style = {};

  return (
    <Grid item xs={4} container direction="column" style={style} spacing={0}>
      <Grid item>
        <Auto {...noteType} />
      </Grid>
      <Grid item>
        <Auto {...emDriver} />
      </Grid>
      {nopp && (
        <Grid item>
          <Auto {...noppCategory} />
        </Grid>
      )}
    </Grid>
  );
}

const ref = {} as any;

function updateMinMax(c: Control, min: number, max: number): void {
  const el: any = c?.inputRef?.current;
  const field = c?.field;

  if (el) {
    el.min = field.min = min === -1 ? 0 : min;
    el.max = field.max = max === -1 ? 0 : max;
  }
  const existingValue = c.value.getState().value;

  if (!isNaN(existingValue) && existingValue >= min && existingValue <= max) {
    c.value.setState({ value: existingValue });
  } else {
    c.value.setState({ value: NaN });
  }

  c.disabled.setState({ value: max === -1 });
}

export function LevelInputs(onReset?: () => void, api?: EmCalculatorStore) {
  const conf = api((s) => s.config);

  const disabled = api((s) => s.readonly);
  const style = { pointerEvents: disabled ? 'none' : undefined };

  const empty = {
    hpi: NaN,
    ros: NaN,
    pfsh: NaN,
    exam: NaN,
    opt: NaN,
    data: NaN,
    risk: NaN,
  };

  const form = useRef(
    formApi({
      values: { ...empty },
      onChange: (v) => {
        ref.hpi = v.hpi;
        ref.ros = v.ros;
        ref.pfsh = v.pfsh;
        ref.exam = v.exam;
        ref.opt = v.opt;
        ref.data = v.data;
        ref.risk = v.risk;

        const request = { ...api.getState().request, ...ref };
        api.setState({ request });

        api.getState().calculateElements(request);
      },
      fields: {
        hpi: {
          label: 'HPI',
          min: conf.minHPI,
          max: conf.maxHPI,
          inputRef: useRef(),
          style,
        },
        ros: {
          label: 'ROS',
          min: conf.minROS,
          max: conf.maxROS,
          inputRef: useRef(),
          style,
        },
        pfsh: {
          label: 'PFSH',
          min: conf.minPFSH,
          max: conf.maxPFSH,
          inputRef: useRef(),
          style,
        },
        exam: {
          label: 'Exam',
          min: conf.minEXAM,
          max: conf.maxEXAM,
          inputRef: useRef(),
          style,
        },
        opt: {
          label: 'Opt',
          min: conf.minOPT,
          max: conf.maxOPT,
          inputRef: useRef(),
          style,
        },
        data: {
          label: 'Data',
          min: conf.minDATA,
          max: conf.maxDATA,
          inputRef: useRef(),
          style,
        },
        risk: {
          label: 'Risk',
          min: conf.minRISK,
          max: conf.maxRISK,
          inputRef: useRef(),
          style,
        },
      },
    })
  ).current;

  const refs = Object.values(form.controls);

  const onKeyUp = (e: any) => {
    refs.forEach((f, i) => {
      if (!f.inputRef || f.inputRef.current !== e.target) {
        return;
      }
      const el = f.inputRef.current as HTMLInputElement;

      if (el) {
        const max = f.field.max.toString().length;

        if (el.value.length === max && i < refs.length - 1) {
          (refs[i + 1] as any).inputRef?.current?.focus();
        }
      }
    });
  };

  useEffect(() => {
    form.disableControls(disabled);
  }, [disabled]);
  
  useEffect(() => {
    refs.forEach((f) => {
      if (f.inputRef && f.inputRef.current) {
        const el: HTMLElement = f.inputRef.current;
        el.addEventListener('keyup', onKeyUp);
      }
    });

    if (api.getState().update) {
      updateMinMax(hpi, conf.minHPI, conf.maxHPI);
      updateMinMax(ros, conf.minROS, conf.maxROS);
      updateMinMax(pfsh, conf.minPFSH, conf.maxPFSH);
      updateMinMax(exam, conf.minEXAM, conf.maxEXAM);
      updateMinMax(opt, conf.minOPT, conf.maxOPT);
      updateMinMax(data, conf.minDATA, conf.maxDATA);
      updateMinMax(risk, conf.minRISK, conf.maxRISK);

      ref.hpi = hpi.value.getState().value;
      ref.ros = ros.value.getState().value;
      ref.pfsh = pfsh.value.getState().value;
      ref.exam = exam.value.getState().value;
      ref.opt = opt.value.getState().value;
      ref.data = data.value.getState().value;
      ref.risk = risk.value.getState().value;

      api.setState({ update: false });
    }



    
    return () =>
      refs.forEach((f) => {
        if (f.inputRef && f.inputRef.current) {
          const el: HTMLElement = f.inputRef.current;
          el.removeEventListener('keyup', onKeyUp);
        }
      });
  });

  const { hpi, ros, pfsh, exam, opt, data, risk } = form.controls;

  const itemStyle = { width: 50 };

  useEffect(() => {
    // console.log('reset calculator api', api.getState());
    if (api.getState().reset) {
      ref.hpi = NaN;
      ref.ros = NaN;
      ref.pfsh = NaN;
      ref.exam = NaN;
      ref.opt = NaN;
      ref.data = NaN;
      ref.risk = NaN;

      form.setValues(empty);

      if (onReset) {
        onReset();
      }

      api.setState({ reset: false });
    }

    const inputs: EmResult = api.getState().inputs;
    if (inputs) {
      form.setValues(inputs);

      if (onReset) {
        onReset();
      }
      api.setState({ inputs: null });
    }
  }, []);
  return (
    <Grid
      container
      direction="row"
      spacing={2}
      justify="center"
      style={{ whiteSpace: 'nowrap', height: '64px', paddingTop: '0px' }}
    >
      <Grid item style={itemStyle}>
        <NumberText {...hpi} />
      </Grid>
      <Grid item style={itemStyle}>
        <NumberText {...ros} />
      </Grid>
      <Grid item style={itemStyle}>
        <NumberText {...pfsh} />
      </Grid>
      <Grid item style={itemStyle}>
        <NumberText {...exam} />
      </Grid>
      <Grid item style={itemStyle}>
        <NumberText {...opt} />
      </Grid>
      <Grid item style={itemStyle}>
        <NumberText {...data} />
      </Grid>
      <Grid item style={itemStyle}>
        <NumberText {...risk} />
      </Grid>
    </Grid>
  );
}

function CriticalCareForm(props: EmCalculatorProps) {
  const { icdCpt, api } = props;
  const disabled = api((s) => s.readonly);
  const form = useRef(
    formApi({
      disabled,
      values: {
        cpt1: false,
        cpt2: false,
        multiplier: 1,
        add: false,
      },
      fields: {
        cpt1: {
          label: '30–74 Minutes',
          onChange: (v) => {
            const disabled = !v;
            cpt2Disabled(disabled);

            if (disabled) {
              addDisabled(disabled);
            }
            const patch: any = { cpt1: v, cpt2: false, add: false };
            if (disabled) {
              cpt2Selected(false);
              addSelected(false);
            }
            updateCritCare(patch);
          },
        },
        cpt2: {
          label: '75–104 Minutes',
          disabled: true,
          onChange: (v) => {
            const disabled = !v;
            const patch: any = { cpt2: v };

            if (disabled) {
              patch.add = false;
              addSelected(false);
            }

            addDisabled(disabled);
            updateCritCare(patch);
          },
        },
        multiplier: {
          label: '',
          name: 'multiplier',
          min: 1,
          max: 100,
          style: { width: '30px' },
          onChange: (n) => {
            if (icdCpt) {
              icdCpt.cpts.actions.visit(
                (v) => v.cptCode === CRITICAL_CARE_CODE_2,
                (v) => {
                  v.multiplier = n || 1;
                }
              );
            }
          },
        },
        add: {
          label: 'Add',
          disabled: true,
          onChange: (v) => {
            updateCritCare({ add: v });
          },
        },
      },
    })
  ).current;

  useEffect(() => {
    form.disableControls(disabled);
  }, [disabled]);
  
  const { cpt1, cpt2, add, multiplier } = form.controls;

  const cpt2Selected = cpt2.selected((s) => s.setValue);
  const cpt2Disabled = cpt2.disabled((s) => s.setValue);

  const addSelected = add.selected((s) => s.setValue);
  const addDisabled = add.disabled((s) => s.setValue);

  const updateCritCare = (patch: any) => {
    const criticalCare = { ...form.values.getState().value, ...patch };
    if(cpt2Disabled){
      criticalCare.multiplier = 1;
      multiplier.value.setState({value: 1});
    }
    api.setState({ criticalCare });
  };

  const nowrap: any = { flexWrap: 'nowrap' };

  return (
    <Grid
      container
      direction="column"
      spacing={0}
      style={{
        paddingTop: '0px',
        paddingRight: '24px',
        paddingLeft: '12px',
      }}
    >
      <Grid item>
        <InputLabel style={{ whiteSpace: 'nowrap', color: 'white' }}>
          Critical Care
        </InputLabel>
      </Grid>
      <Grid item style={nowrap}>
        <CheckBox {...cpt1} />
      </Grid>
      <Grid item style={nowrap}>
        <CheckBox {...cpt2} />
      </Grid>
      <Grid
        item
        container
        direction="row"
        alignItems="center"
        spacing={2}
        style={nowrap}
      >
        <Grid item>
          <NumberText {...multiplier} />
        </Grid>
        <Grid item>
          <InputLabel style={{ whiteSpace: 'nowrap', color: 'white' }}>
            Additional CC
          </InputLabel>
        </Grid>
      </Grid>
    </Grid>
  );
}

function Summary({ icdCpt, api }) {
  const emResult = api((s) => s.result);
  const result: Partial<EmResult> = emResult || ({} as any);
  const errors = result.successful
    ? ''
    : (result.errorMessages || []).join(' ');

  const style: any = { whiteSpace: 'nowrap' };
  const error = {
    ...style,
    ...{
      color: 'white',
      paddingTop: 6,
      fontSize: 11,
      whiteSpace: 'break-spaces',
      width: '100%',
    },
  };

  const onSelectResult = (key: string) => {
    return () => {
      if (icdCpt) {
        icdCpt.setCode(result[key]);
      }
    };
  };

  return (
    <>
      <Grid
        item
        container
        direction="row"
        justify="center"
        alignItems="center"
        spacing={1}
      >
        {result.noppEM > 0 && (
          <Grid item>
            <FieldValue
              spacing={0}
              row={true}
              label="NOPP EM"
              value={result.noppEM}
            />
          </Grid>
        )}
        {result.noppEM > 0 && (
          <AddButton tooltip="Add CPT" onClick={onSelectResult('noppEM')} />
        )}
        {result.timeBasedEM > 0 && (
          <Grid item>
            <FieldValue
              spacing={0}
              row={true}
              label="Time Based EM"
              value={result.timeBasedEM}
            />
          </Grid>
        )}
        {result.timeBasedEM > 0 && (
          <AddButton
            tooltip="Add CPT"
            onClick={onSelectResult('timeBasedEM')}
          />
        )}
        {result.calculatedEM > 0 && (
          <Grid item>
            <FieldValue
              spacing={0}
              row={true}
              label="Calculated EM"
              value={result.calculatedEM}
            />
          </Grid>
        )}
        {result.calculatedEM > 0 && (
          <AddButton
            tooltip="Add CPT"
            onClick={onSelectResult('calculatedEM')}
          />
        )}
      </Grid>
      {!result.successful && (
        <Grid item style={{ width: '100%' }} container justify="center">
          <div style={error}>{errors.length ? errors : ''}</div>
        </Grid>
      )}
    </>
  );
}

const options = [
  {
    label: 'Element',
    value: 'element',
    selected: true,
  },
  {
    label: 'Time ',
    value: 'time',
    disabled: false,
  },
];

export function EmCalculator(props: EmCalculatorProps) {
  const { icdCpt, api } = props;

  const conf = api((s) => s.config);
  const [timeList, setTimeList] = useState([]);
  const inputs = LevelInputs(() => form.reset(), api);

  const disabled = api((s) => s.readonly);

  const typeForm = useRef(
    formApi({
      disabled,
      values: { calcType: 'element', timeRange: '' },
      fields: {
        calcType: {
          options,
          row: true,
          style: {},
        },
        timeRange: {
          options: timeList.map((t) => ({
            label: t,
            value: t,
          })),
          onChange: (timeRange) =>
            calc({ ...api.getState().request, ...{ timeRange } }),
          style: { height: '48px', display: 'block' },
        },
      },
    })
  ).current;

  const { calcType: calcT, timeRange } = typeForm.controls;

  const setTimeRangeOptions = timeRange.options((s) => s.setValue);
  const setCalcTypeOptions = calcT.options((s) => s.setValue);

  const calcType = calcT.value((s) => s.value);
  const setCalType = calcT.value((s) => s.setValue);

  const calc = (request: any) => {
    if (calcT.value.getState().value !== 'time') {
      api.getState().calculateElements(request);
    } else {
      api.getState().calculateTime(request);
    }
  };

  const updateTimeList = (v: Driver) => {
    // console.log('updateTimeList', v)

    const disableTime = !v.timeRangeList.length;
    options[1].disabled = disableTime;

    //TODO: ask if type should always be set on driver change
    if (disableTime) {
      setCalType('element');
    }

    setTimeList(v.timeRangeList);
    setTimeRangeOptions(
      v.timeRangeList.map((t: string) => ({ label: t, value: t }))
    );
    setCalcTypeOptions(options.slice());
  };

  const timeRanges = <RadioGroup {...timeRange} />;

  const form = useRef(
    serviceCategories({
      api,
      conf,
      onCategoryChange: () => {
        //  console.log('onCategoryChange element ')
        typeForm.setValues({ calcType: 'element' });
        setCalType('element');
        options[1].disabled = true;
      },
      onDriverChange: (v) => updateTimeList(v),
    })
  ).current;


  useEffect(() => {
    form.disableControls(disabled);
  
    return api.subscribe(
      (request) => {
        calc(request);
      },
      (state) => state.request
    );
  }, [disabled]);

  const controlValues = timeRange.options((s) => s.value);
  const opt = calcT.options((s) => s.value);
  opt[1].disabled = controlValues.length === 0;

  return (
    <div
      style={{
        width: '100%',
        minHeight: 156,
        color: 'white',
        paddingLeft: 12,
        paddingRight: 12,
        paddingTop: 6,
        paddingBottom: 12,
        borderBottom: `solid 1px ${lightGray}`,
      }}
    >
      <Grid
        item
        container
        direction="row"
        wrap="nowrap"
        style={{ width: '100%' }}
        alignItems="flex-start"
        justify="center"
        spacing={1}
      >
        <ServiceCategories api={api} form={form} nopp={calcType !== 'time'} />
        {api.getState().required && (
          <Grid
            style={{
              color: 'white',
              position: 'relative',
              whiteSpace: 'nowrap',
              fontStyle: 'italic',
              marginTop: 12
            }}
          >
            * Required For EM Codes
          </Grid>
        )}
        <Grid
          item
          xs={6}
          container
          direction="column"
          spacing={1}
          wrap={'nowrap'}
          style={{ paddingTop: 12, flexGrow: 1 }}
          alignItems="center"
          justify="center"
        >
          <RadioGroup {...typeForm.controls.calcType} />

          {calcType !== 'time' ? inputs : timeRanges}

          <Summary api={api} icdCpt={icdCpt} />
        </Grid>

        <Grid xs={2} item style={{ paddingTop: 16 }}>
          <CriticalCareForm {...props} />
        </Grid>
      </Grid>
    </div>
  );
}
